import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const empty = { project_code: "", project_name: "", contract_number: "", client_id: "", location: "", start_date: "", end_date: "", status: "active" };

export default function ProjectMaster() {
  const { has } = useAuth();
  const canManageClients = has("PROJECT.CREATE");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [refresh, setRefresh] = useState(0);

  // client master state
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState("");
  const [addingClient, setAddingClient] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const r = await api.get("/clients");
      setClients(r.data.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...empty, ...row, client_id: row.client_id || "" }); setOpen(true); };

  const addClientInline = async () => {
    const name = newClient.trim();
    if (!name) return;
    setAddingClient(true);
    try {
      const r = await api.post("/clients", { name });
      toast.success("Client added");
      setNewClient("");
      await loadClients();
      setForm((f) => ({ ...f, client_id: r.data.id }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add client");
    } finally {
      setAddingClient(false);
    }
  };

  const save = async () => {
    if (!form.project_name.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      if (editing) {
        const { project_code, ...rest } = form;
        await api.put(`/projects/${editing.id}`, rest);
        toast.success("Project updated");
      } else {
        const { project_code, ...rest } = form;
        await api.post("/projects", rest);
        toast.success("Project created");
      }
      setOpen(false);
      setRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/projects/${row.id}/status?is_active=${!row.is_active}`);
      toast.success(row.is_active ? "Deactivated" : "Activated");
      setRefresh((n) => n + 1);
    } catch (e) { toast.error("Action failed"); }
  };

  const columns = [
    { key: "project_code", label: "Code", mono: true },
    { key: "project_name", label: "Project Name", render: (r) => <span className="font-medium text-slate-900">{r.project_name}</span> },
    { key: "client", label: "Client" },
    { key: "location", label: "Location", hideMobile: true },
    { key: "status", label: "Status", render: (r) => <Badge variant={r.is_active ? "default" : "secondary"} className={r.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}>{r.is_active ? "Active" : "Inactive"}</Badge> },
  ];

  const activeClientOptions = clients.filter((c) => c.is_active !== false).map((c) => ({ value: c.id, label: c.name }));

  const projectsTab = (
    <>
      <div className="mb-4 flex justify-end">
        {has("PROJECT.CREATE") && <Button data-testid="project-add" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> New Project</Button>}
      </div>
      <DataTable
        testid="projects-table"
        columns={columns}
        searchPlaceholder="Search by code, name, client..."
        refreshKey={refresh}
        load={(params) => api.get("/projects", { params }).then((r) => r.data)}
        rowActions={(row) => (
          <div className="flex justify-end gap-1">
            {has("PROJECT.EDIT") && <Button data-testid={`project-edit-${row.id}`} variant="ghost" size="sm" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>}
            {has("PROJECT.DEACTIVATE") && <Button data-testid={`project-toggle-${row.id}`} variant="ghost" size="sm" onClick={() => toggle(row)}><Power className={`h-4 w-4 ${row.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>}
          </div>
        )}
      />
    </>
  );

  return (
    <div>
      <PageHeader title="Project Master" subtitle="Manage projects / contracts and clients" />

      {canManageClients ? (
        <Tabs defaultValue="projects">
          <TabsList data-testid="pm-tabs">
            <TabsTrigger value="projects" data-testid="pm-tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="clients" data-testid="pm-tab-clients">Client Master</TabsTrigger>
          </TabsList>
          <TabsContent value="projects" className="mt-4">{projectsTab}</TabsContent>
          <TabsContent value="clients" className="mt-4">
            <ClientMaster clients={clients} reload={loadClients} />
          </TabsContent>
        </Tabs>
      ) : projectsTab}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {editing && (
              <div><Label>Project Code</Label><Input data-testid="project-code-input" disabled value={form.project_code} className="mt-1" /></div>
            )}
            <div className={editing ? "" : "col-span-2"}><Label>Project Name *</Label><Input data-testid="project-name-input" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className="mt-1" /></div>
            {!editing && <p className="col-span-2 -mt-2 text-xs text-slate-400">Project code is auto-generated (01, 02, 03…) on save.</p>}
            <div><Label>Contract No.</Label><Input value={form.contract_number} onChange={(e) => setForm({ ...form, contract_number: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Client</Label>
              <SearchableSelect
                testid="project-client-select"
                value={form.client_id}
                onChange={(v) => setForm({ ...form, client_id: v })}
                options={activeClientOptions}
                placeholder="Select client…"
                className="mt-1"
              />
            </div>
            {canManageClients && (
              <div className="col-span-2 flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Add new client</Label>
                  <Input data-testid="project-new-client-input" value={newClient} onChange={(e) => setNewClient(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addClientInline(); } }} placeholder="New client name" className="mt-1" />
                </div>
                <Button type="button" variant="outline" data-testid="project-add-client-btn" disabled={addingClient || !newClient.trim()} onClick={addClientInline}><Plus className="mr-1 h-4 w-4" /> Add</Button>
              </div>
            )}
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
            <div><Label>Start Date</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" /></div>
            <div><Label>End Date</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="project-save" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientMaster({ clients, reload }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      await api.post("/clients", { name: n });
      toast.success("Client added");
      setName("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add client");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    const active = c.is_active !== false;
    try {
      await api.patch(`/clients/${c.id}/status?is_active=${!active}`);
      toast.success(active ? "Deactivated" : "Activated");
      reload();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex-1 max-w-sm">
          <Label>New Client Name</Label>
          <Input data-testid="client-name-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="e.g. MSEDCL" className="mt-1" />
        </div>
        <Button data-testid="client-add-btn" disabled={saving || !name.trim()} onClick={add} className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> Add Client</Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="clients-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100">
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Client Name</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-10 text-center text-sm text-slate-400">No clients yet</td></tr>
            ) : clients.map((c) => {
              const active = c.is_active !== false;
              return (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm font-medium text-slate-900">{c.name}</td>
                  <td className="px-3 py-2 text-sm"><Badge variant={active ? "default" : "secondary"} className={active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}>{active ? "Active" : "Inactive"}</Badge></td>
                  <td className="px-3 py-2 text-right">
                    <Button data-testid={`client-toggle-${c.id}`} variant="ghost" size="sm" onClick={() => toggle(c)}><Power className={`h-4 w-4 ${active ? "text-red-500" : "text-emerald-500"}`} /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
